/**
 * src/lib/hooks/useNotifications.js — In-app notifications hook (TICKET-030).
 *
 * Subscribes to Supabase realtime postgres_changes on the notifications table
 * filtered to the current user. Returns the live unread set + helpers to mark
 * read.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { supabase } from '../supabase';
import {
  fetchNotifications,
  markNotificationRead as apiMarkRead,
  markAllRead as apiMarkAllRead,
  subscribeToNotifications,
} from '../api/notifications';

export default function useNotifications({ unreadOnly = true } = {}) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const rows = await fetchNotifications({ unreadOnly });
      setNotifications(rows);
    } finally {
      setLoading(false);
    }
  }, [userId, unreadOnly]);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return undefined;
    }
    reload();

    const channel = subscribeToNotifications(userId, (row) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === row.id)) return prev;
        return [row, ...prev];
      });
    });

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [userId, reload]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at).length,
    [notifications],
  );

  const markRead = useCallback(async (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n,
      ),
    );
    const result = await apiMarkRead(notificationId);
    if (!result.success) {
      // Revert on failure.
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read_at: null } : n)),
      );
    }
    return result;
  }, []);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now })));
    const result = await apiMarkAllRead();
    if (!result.success) reload();
    return result;
  }, [reload]);

  return { notifications, unreadCount, loading, reload, markRead, markAllRead };
}
